import { toast } from '@/hooks/use-toast';
import { anonymizeStudy, getAnonymizeJob } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
/** Poll cadence while the copy is being produced. */
export const ANONYMIZE_JOB_POLL_MS = 2_000;
const TERMINAL = new Set(['completed', 'failed']);
export function useAnonymizeStudy() {
    return useMutation({
        mutationFn: ({ studyId, body }) => anonymizeStudy(studyId, body),
    });
}
/**
 * One job's progress. Polls every 2s until the job is completed or failed, then
 * stops; the worklist is told once, through `onSettled`, so the new study appears
 * (or the failure is reported) without the caller watching the query itself.
 */
function useAnonymizeJob(jobId, onSettled) {
    const query = useQuery({
        queryKey: ['study-anonymize-job', jobId],
        queryFn: () => getAnonymizeJob(jobId),
        enabled: !!jobId,
        refetchInterval: (q) => {
            const state = q.state.data?.state;
            return state && TERMINAL.has(state) ? false : ANONYMIZE_JOB_POLL_MS;
        },
        refetchIntervalInBackground: false,
    });
    // Fire the settle callback exactly once per job, on the transition into a
    // terminal state, however many renders observe that state afterwards.
    const settledFor = useRef(null);
    const onSettledRef = useRef(onSettled);
    useEffect(() => {
        onSettledRef.current = onSettled;
    }, [onSettled]);
    const status = query.data;
    useEffect(() => {
        if (!status || !TERMINAL.has(status.state) || settledFor.current === status.jobId)
            return;
        settledFor.current = status.jobId;
        onSettledRef.current?.(status);
    }, [status]);
    return query;
}
/**
 * Headless watcher mounted by the worklist for each job it started: toasts the
 * outcome and refreshes the list so the new study shows up. Several can run at
 * once; each watches one job.
 */
export function AnonymizeJobWatcher({ jobId, onDone, }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    useAnonymizeJob(jobId, (status) => {
        if (status.state === 'completed') {
            const copied = status.processedImages - status.skippedImages;
            toast({
                title: t('anonymize.ready'),
                description: status.skippedImages > 0
                    ? t('anonymize.readyDescSkipped', { count: copied, skipped: status.skippedImages })
                    : t('anonymize.readyDesc', { count: copied }),
            });
            void queryClient.invalidateQueries({ queryKey: ['studies'] });
        }
        else {
            toast({
                title: t('anonymize.failed'),
                description: status.error ?? t('anonymize.failedDesc'),
                variant: 'destructive',
            });
        }
        onDone(status);
    });
    return null;
}
